import { DEF_MONTH_FORMAT, GlobalUtils } from "@/app/home/utils/global.utils";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import container from "@/lib/di/container";
import { Alert, Checkbox, ConfigProvider, DatePicker, Form, Input, Modal, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

const waterInvoiceRepo = container.get(WaterInvoiceRepo);

interface CancelAllMonthForm {
    date: Dayjs;
    cancelReason: string;
    confirmCancelPublishedInvoice: boolean;
}

interface CancelAllMonthModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: {
        month?: string;
        year?: string;
    };
}

const INPUT_WIDTH = 450;

export const CancelAllMonthModal: React.FC<CancelAllMonthModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    data,
}) => {
    const [form] = Form.useForm<CancelAllMonthForm>();
    const [loading, setLoading] = useState(false);
    const defaultFilter = GlobalUtils.getPreviousMonthFilter();

    useEffect(() => {
        if (!isModalOpen) return;
        const month = parseInt(data.month || defaultFilter.month.value, 10);
        const year = parseInt(data.year || defaultFilter.year.value, 10);
        form.setFieldsValue({
            date: dayjs(new Date(year, month - 1)),
            cancelReason: "",
            confirmCancelPublishedInvoice: false,
        });
    }, [isModalOpen, data.month, data.year]);

    const handleCancel = () => {
        if (loading) return;
        setIsModalOpen(false);
    };

    const doSubmit = async (
        month: number,
        year: number,
        cancelReason: string,
        confirmCancelPublishedInvoice: boolean,
    ) => {
        setLoading(true);
        const result = await waterInvoiceRepo.cancelInvoicesByMonth({
            year,
            month,
            cancelReason,
            confirmCancelPublishedInvoice,
        });
        if (result.success) {
            message.success(result.message);
            setIsModalOpen(false);
        } else {
            message.error(result.message);
        }
        setLoading(false);
    };

    const handleSubmit = async () => {
        try {
            const formData = await form.validateFields();
            const cancelReason = (formData.cancelReason ?? "").trim();
            const date = formData.date;
            if (!date || !cancelReason) {
                message.error("Vui lòng chọn tháng và nhập lý do hủy");
                return;
            }

            const month = date.month() + 1;
            const year = date.year();
            if (month < 1 || month > 12) {
                message.error("Tháng không hợp lệ");
                return;
            }

            const confirmCancelPublishedInvoice = !!formData.confirmCancelPublishedInvoice;

            Modal.confirm({
                title: "Xác nhận hủy tất cả hóa đơn tháng",
                content: (
                    <div>
                        <p>
                            Thao tác này sẽ hủy <b>toàn bộ hóa đơn của nhà máy</b> trong tháng{" "}
                            <b>
                                {month}/{year}
                            </b>
                            , không giới hạn theo bộ lọc hoặc dòng đang chọn trên màn hình.
                        </p>
                        <p className="mt-2">Bạn có chắc chắn muốn tiếp tục?</p>
                    </div>
                ),
                okText: "Hủy tất cả hóa đơn tháng",
                cancelText: "Đóng",
                okButtonProps: { danger: true },
                onOk: () => doSubmit(month, year, cancelReason, confirmCancelPublishedInvoice),
            });
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={560}
                style={{ top: 40 }}
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                okText="Tiếp tục"
                cancelText="Đóng"
                okButtonProps={{
                    danger: true,
                    loading,
                    disabled: loading,
                }}
                cancelButtonProps={{ disabled: loading }}
                onOk={handleSubmit}
                destroyOnClose
            >
                <div className="flex flex-row w-full items-start justify-start select-none mb-4">
                    <p className="ml-2 text-2xl font-bold text-primary">HỦY TẤT CẢ HÓA ĐƠN THÁNG</p>
                </div>

                <Alert
                    type="error"
                    showIcon
                    className="mb-4"
                    message="Phạm vi toàn nhà máy"
                    description="API sẽ hủy toàn bộ hóa đơn của nhà máy theo tháng/năm đã chọn. Thao tác không áp dụng theo khu vực, hộ dùng nước hay các dòng đang lọc/chọn trên màn hình."
                />

                <Form<CancelAllMonthForm>
                    form={form}
                    layout="vertical"
                    className="px-1"
                    initialValues={{ confirmCancelPublishedInvoice: false }}
                >
                    <Form.Item
                        label="Kỳ hóa đơn"
                        name="date"
                        rules={[{ required: true, message: "Vui lòng chọn tháng" }]}
                    >
                        <DatePicker.MonthPicker
                            format={DEF_MONTH_FORMAT}
                            style={{ width: INPUT_WIDTH }}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Lý do hủy"
                        name="cancelReason"
                        rules={[
                            { required: true, message: "Vui lòng nhập lý do hủy" },
                            {
                                validator: (_, value) =>
                                    (value ?? "").trim()
                                        ? Promise.resolve()
                                        : Promise.reject(new Error("Vui lòng nhập lý do hủy")),
                            },
                        ]}
                    >
                        <Input.TextArea
                            placeholder="Nhập lý do hủy tất cả hóa đơn tháng"
                            style={{ width: INPUT_WIDTH, height: 120, resize: "none" }}
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                    <Form.Item
                        name="confirmCancelPublishedInvoice"
                        valuePropName="checked"
                        className="mb-2"
                    >
                        <Checkbox>
                            Tôi xác nhận vẫn hủy hóa đơn dù hóa đơn điện tử đã được phát hành
                        </Checkbox>
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        className="mb-2"
                        message="Lưu ý về hóa đơn điện tử"
                        description="Thao tác này chỉ hủy hóa đơn trong hệ thống nội bộ. Không tự hủy hóa đơn điện tử tại nhà cung cấp (EasyInvoice/MatBao) và không xóa số HĐĐT đã phát hành."
                    />
                </Form>
            </Modal>
        </ConfigProvider>
    );
};
